import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SignOut, House, CalendarBlank, CurrencyDollar, FileText, Moon, Sun, FolderOpen } from '@phosphor-icons/react';
import { useTheme } from '../contexts/ThemeContext';
import { BackButton } from './BackButton';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_objectif-ecosystem/artifacts/4tz74flx_LOGO%20OBJECTIF%20SCOP%20OUTREMER%202%20%283%29.png';

export default function ClientLayout({ children, activePage }) {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [clientUser, setClientUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/client/me`, { withCredentials: true });
      setClientUser(data);
    } catch {
      navigate('/espace-client/login');
    }
  }, [navigate]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/api/client/auth/logout`, {}, { withCredentials: true });
    } catch {}
    navigate('/espace-client/login');
  };

  const navItems = [
    { key: 'dashboard', label: 'Tableau de bord', icon: House, path: '/espace-client' },
    { key: 'dossiers', label: 'Dossiers', icon: FolderOpen, path: '/espace-client/dossiers' },
    { key: 'echeanciers', label: 'Echeanciers', icon: CalendarBlank, path: '/espace-client/echeanciers' },
    { key: 'paiements', label: 'Paiements', icon: CurrencyDollar, path: '/espace-client/paiements' },
    { key: 'documents', label: 'Documents', icon: FileText, path: '/espace-client/documents' },
  ];

  const currentPage = activePage || navItems.find(n => n.path === location.pathname)?.key || 'dashboard';

  return (
    <div className="min-h-screen bg-[#F9F8F5]">
      <header className="bg-white border-b border-[#E8E6E1] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BackButton fallback="/espace-client" testId="client-back-btn" />
            <img src={LOGO_URL} alt="Objectif SCOP Outremer" className="w-9 h-9 object-contain" data-testid="client-header-logo" />
            <div>
              <p className="text-sm font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Espace Client</p>
              <p className="text-[10px] text-[#5E6D66]">{clientUser?.organization?.raison_sociale || clientUser?.name || ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={toggleTheme} className="p-2 hover:bg-[#F9F8F5] rounded-lg transition-colors" data-testid="client-theme-toggle">
              {theme === 'dark' ? <Sun size={18} className="text-amber-400" weight="fill" /> : <Moon size={18} className="text-[#5E6D66]" />}
            </button>
            <span className="text-xs text-[#5E6D66] hidden sm:block">{clientUser?.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[#5E6D66]" data-testid="client-logout-btn">
              <SignOut size={18} />
            </Button>
          </div>
        </div>
        {/* Nav tabs */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <nav className="flex gap-1 -mb-px overflow-x-auto" data-testid="client-nav">
            {navItems.map((item) => {
              const isActive = currentPage === item.key;
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  onClick={() => navigate(item.path)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-[#00AEEF] text-[#00AEEF]'
                      : 'border-transparent text-[#5E6D66] hover:text-[#1A2421] hover:border-[#E8E6E1]'
                  }`}
                  data-testid={`client-nav-${item.key}`}
                >
                  <Icon size={16} weight={isActive ? 'fill' : 'regular'} />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>
        {/* Brand bar */}
        <div className="flex h-[3px]">
          <div className="flex-1" style={{ background: '#00AEEF' }} />
          <div className="flex-1" style={{ background: '#80C51F' }} />
          <div className="flex-1" style={{ background: '#EA3C3A' }} />
          <div className="flex-1" style={{ background: '#FF8E00' }} />
          <div className="flex-1" style={{ background: '#D5007D' }} />
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">{children}</main>
    </div>
  );
}
